import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Drawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CheckIcon from '@mui/icons-material/Check';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import getServices from '../../Services/get-service';
import postService from '../../Services/post-service';
import putServices from '../../Services/put-service';
import swal from 'sweetalert';
import Loading from '../SelectValue/Loading';
import Constant from '../../Constant';
import TopDCom from '../Savings.js/TopDCom';

const drawerWidth = 560;

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return `NGN ${Constant.FormatNumber(value)}`;
};

const formatDate = (value) => {
  if (!value) return 'No date';
  return value.slice(0, 10);
};

const formatScheduleDate = (value) => value || '';

const getFullName = (item) => {
  const name = `${item?.firstName || ''} ${item?.lastName || ''}`.trim();
  return name || item?.userFullName || 'N/A';
};

const getStatusStyles = (status = '') => {
  switch (status.toUpperCase()) {
    case 'PAID':
      return {
        dot: 'bg-green-700 text-white',
        text: 'text-green-800',
        icon: <CheckIcon sx={{ fontSize: 14 }} />,
      };
    case 'OVERDUE':
    case 'FAILED':
      return {
        dot: 'bg-red-100 text-red-600',
        text: 'text-red-600',
        icon: <InfoOutlinedIcon sx={{ fontSize: 14 }} />,
      };
    case 'PENDING':
    case 'UPCOMING':
      return {
        dot: 'bg-[#F4EEFF] text-back-color2',
        text: 'text-back-color2',
        icon: <NotificationsNoneOutlinedIcon sx={{ fontSize: 14 }} />,
      };
    default:
      return {
        dot: 'bg-[#F4EEFF] text-[#8CA897]',
        text: 'text-[#8CA897]',
        icon: null,
      };
  }
};

const hasOverdueSchedule = (schedule = []) => {
  return schedule.some((item) => {
    const statusText = `${item.mainStatus || ''} ${item.uiStatus || ''} ${item.statusDescription || ''}`.toUpperCase();
    return statusText.includes('OVERDUE') || statusText.includes('LATE') || statusText.includes('FAILED');
  });
};

const hasDeliveredSignal = (item) => {
  if (!item) return false;

  const statusText = [
    item.status,
    item.deliveryStatus,
    item.assetDeliveryStatus,
    item.requestDeliveryStatus,
  ].filter(Boolean).join(' ').toUpperCase();

  return Boolean(
    item.delivered ||
    item.isDelivered ||
    item.deliveryConfirmed ||
    statusText.includes('DELIVERED') ||
    Number(item.installmentCount) > 0 ||
    Number(item.paidInstallmentCount) > 0
  );
};

function RepaymentDetailsDrawer({ open, requestId, overviewItem, onClose, onDelivered }) {
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [message, setMessage] = useState('');
  const [deliveredLocally, setDeliveredLocally] = useState(false);

  const fetchSchedule = () => {
    if (!requestId || !open) return;

    setLoading(true);
    getServices.getAssetRepaymentSchedule(requestId).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setSchedule(response.data.data || null);
        } else {
          setSchedule({ scheduleTimeline: [] });
        }
      },
      (error) => {
        setLoading(false);
        setSchedule({ scheduleTimeline: [] });
      }
    );
  };

  useEffect(() => {
    if (!open) return;
    setSchedule(null);
    setShowMessageBox(false);
    setMessage('');
    setDeliveredLocally(false);
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  const sendReminder = () => {
    if (!requestId) return;
    setLoading(true);
    postService.sendAssetRepaymentReminder(requestId).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          swal(response.data.description || 'Repayment reminder alert dispatched successfully.');
        } else {
          swal(response.data.description || 'Failed to send repayment reminder');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error sending repayment reminder');
      }
    );
  };

  const sendMessage = () => {
    if (!requestId || !message.trim()) {
      swal('Please enter a message');
      return;
    }

    setLoading(true);
    postService.sendAssetRepaymentMessage(requestId, { message: message.trim() }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          swal(response.data.description || 'Custom message sent successfully');
          setMessage('');
          setShowMessageBox(false);
        } else {
          swal(response.data.description || 'Failed to send custom message');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error sending custom message');
      }
    );
  };

  const markAsDelivered = () => {
    if (!requestId) return;

    swal({
      title: 'Mark asset as delivered?',
      text: 'Are you sure you want to mark this asset as delivered?',
      icon: 'warning',
      buttons: ['No', 'Yes'],
      dangerMode: false,
    }).then((confirmed) => {
      if (!confirmed) return;

      setLoading(true);
      putServices.markAssetRequestDelivered(requestId, {}).then(
        (response) => {
          setLoading(false);
          if (response.data.status === '2000') {
            setDeliveredLocally(true);
            swal(response.data.description || 'Asset marked as delivered successfully.').then(() => {
              fetchSchedule();
              if (onDelivered) onDelivered();
            });
          } else {
            swal(response.data.description || 'Failed to mark asset as delivered');
          }
        },
        (error) => {
          setLoading(false);
          swal(error?.response?.data?.description || 'Error marking asset as delivered');
        }
      );
    });
  };

  const scheduleTimeline = schedule?.scheduleTimeline || [];
  const deliveryIsConfirmed = deliveredLocally || hasDeliveredSignal(overviewItem);
  const scheduleIsActive = deliveryIsConfirmed && scheduleTimeline.length > 0;
  const overdue = hasOverdueSchedule(scheduleTimeline);
  const details = {
    userFullName: schedule?.userFullName || getFullName(overviewItem),
    userEmail: schedule?.userEmail || overviewItem?.email,
    userInitials: schedule?.userInitials || getFullName(overviewItem).split(' ').map((word) => word.charAt(0)).slice(0, 2).join('') || 'NA',
    productName: schedule?.productName || overviewItem?.assetName,
    totalAmount: schedule?.totalAmount ?? overviewItem?.totalAmountPayable,
    monthlyInstallment: schedule?.monthlyInstallment,
    tenureMonths: schedule?.tenureMonths ?? overviewItem?.tenureMonth,
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '82%', md: drawerWidth },
          boxSizing: 'border-box',
          backgroundColor: 'white',
        },
      }}
    >
      <div className="relative flex h-full flex-col px-5 py-6">
        <Loading open={loading} variant="local" />
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-back-color2">Repayment Details</h1>
            {overdue ? (
              <span className="rounded-lg bg-red-50 px-3 py-1 text-xs font-bold text-red-600">Overdue</span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-back-color2 hover:bg-gray-100"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {!schedule ? (
          <div className="flex-1" />
        ) : (
          <div className="flex flex-1 flex-col overflow-y-auto pr-1">
            <div className="rounded-lg border border-[#EEE7FC] p-5">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-violet-500 text-2xl font-bold text-white">
                  {details.userInitials || 'NA'}
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-bold capitalize text-back-color2">{details.userFullName || 'N/A'}</h2>
                  <p className="break-words text-sm text-[#6F6780]">{details.userEmail || 'No email'}</p>
                </div>
              </div>

              <div className="my-5 border-t border-[#EEE7FC]" />

              <p className="mb-2 text-xs text-[#4B3A6A]">Requested Asset</p>
              <p className="mb-4 text-sm font-bold text-primary-color">
                {details.productName || 'N/A'} {details.totalAmount ? `(${formatCurrency(details.totalAmount)})` : ''}
              </p>

              <div className="grid grid-cols-3 gap-4 rounded-2xl bg-[#F4EEFF] p-4">
                <div>
                  <p className="mb-2 text-xs uppercase text-[#4B3A6A]">Total amount</p>
                  <p className="text-sm font-semibold text-back-color2">{formatCurrency(details.totalAmount)}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs uppercase text-[#4B3A6A]">Monthly</p>
                  <p className="text-sm font-semibold text-back-color2">{formatCurrency(details.monthlyInstallment)}</p>
                </div>
                <div>
                  <p className="mb-2 text-xs uppercase text-[#4B3A6A]">Tenure</p>
                  <p className="text-sm font-semibold text-back-color2">{details.tenureMonths || 0} Months</p>
                </div>
              </div>
            </div>

            {!scheduleIsActive ? (
              <div className="mt-6 flex flex-1 flex-col">
                <h2 className="mb-4 text-lg font-bold text-back-color2">Delivery Confirmation</h2>
                <div className="flex gap-2 rounded-lg bg-[#F4EEFF] p-4 text-sm text-back-color2">
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  <p>Mark this asset as delivered to start the customer’s repayment tenure.</p>
                </div>
                <div className="sticky bottom-0 mt-auto bg-white pt-4 pb-6">
                  <button
                    type="button"
                    onClick={markAsDelivered}
                    disabled={loading}
                    className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white disabled:bg-gray-300"
                  >
                    Mark as delivered
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-5 rounded-lg bg-white p-5 shadow-[0_18px_40px_rgba(22,13,55,0.08)]">
                  <h2 className="mb-6 text-base font-bold text-back-color2">Repayment Schedule</h2>
                  <div className="space-y-0">
                    {scheduleTimeline.map((item, index) => {
                      const styles = getStatusStyles(item.uiStatus || item.mainStatus);
                      const isLast = index === scheduleTimeline.length - 1;

                      return (
                        <div key={item.scheduleId || `${item.installmentMonth}-${index}`} className="grid grid-cols-[40px_1fr_auto] gap-3">
                          <div className="relative flex justify-center">
                            {!isLast ? <span className="absolute top-8 h-full w-px bg-[#E6E1F0]" /> : null}
                            <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${styles.dot}`}>
                              {styles.icon}
                            </span>
                          </div>
                          <div className="pb-7">
                            <p className={`text-sm font-bold ${styles.text}`}>
                              {item.monthName || `Month ${item.installmentMonth}`} • {formatCurrency(item.amountDue)}
                            </p>
                            <p className="mt-1 text-xs text-green-900">{item.statusDescription || 'Upcoming'}</p>
                          </div>
                          <p className={`pt-1 text-right text-xs ${styles.text}`}>
                            {formatScheduleDate(item.dateLabel)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 space-y-3 pb-6">
                  <div className="flex gap-2 rounded-lg bg-[#F4EEFF] p-3 text-xs text-back-color2">
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    <p>Send a reminder to the customer regarding their overdue payment to avoid asset recovery procedures.</p>
                  </div>

                  {!showMessageBox ? (
                    <>
                      <button
                        type="button"
                        onClick={sendReminder}
                        disabled={loading}
                        className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white disabled:bg-gray-300"
                      >
                        Send Payment Reminder
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowMessageBox(true)}
                        className="w-full rounded-lg border border-[#E4D7FB] bg-[#F8F3FF] px-4 py-4 font-bold text-primary-color"
                      >
                        Send Custom Message
                      </button>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-back-color2">Custom Message</label>
                        <textarea
                          value={message}
                          onChange={(event) => setMessage(event.target.value)}
                          className="min-h-[150px] w-full resize-none rounded-lg border-0 bg-[#F7F8FB] p-4 outline-none focus:ring-1 focus:ring-primary-color"
                          placeholder="Type your message to the customer here..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={sendMessage}
                        disabled={loading || !message.trim()}
                        className="w-full rounded-lg bg-violet-500 px-4 py-4 font-bold text-white disabled:bg-gray-300"
                      >
                        Send Message
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default function RepaymentTenure() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const fetchOverview = () => {
    setLoading(true);
    getServices.getAssetRepaymentsOverview({
      page,
      limit: rowsPerPage,
    }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setAssets(response.data.data?.assets || []);
          setPagination(response.data.data?.pagination || null);
        } else {
          swal(response.data.description || 'Failed to fetch repayment overview');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error fetching repayment overview');
      }
    );
  };

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    getServices.getAssetProductStats().then(
      (response) => {
        if (response.data.status === '2000') {
          setAssetStats(response.data.data || null);
        }
      },
      () => {}
    );
  }, []);

  const visibleAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assets;

    return assets.filter((item) => {
      const searchText = [
        getFullName(item),
        item.email,
        item.assetName,
        item.status,
      ].filter(Boolean).join(' ').toLowerCase();

      return searchText.includes(query);
    });
  }, [assets, search]);

  const handlePreviousPage = () => {
    if (pagination?.hasPrevious || page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setPage(page + 1);
    }
  };

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.currentPage ?? page;

  return (
    <>
      <Loading open={loading} />
      <RepaymentDetailsDrawer
        open={Boolean(selectedRequestId)}
        requestId={selectedRequestId}
        overviewItem={selectedAsset}
        onClose={() => {
          setSelectedRequestId(null);
          setSelectedAsset(null);
        }}
        onDelivered={fetchOverview}
      />

      <div className="my-8 flex flex-wrap items-center gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="w-full sm:w-auto lg:col-span-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by user or asset name"
              className="border border-gray-300 focus:border-primary-color outline-none p-3 rounded w-full"
            />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 md:grid-cols-5 mb-10 gap-4">
        <TopDCom name="Total assets in inventory" count={assetStats?.totalAssetsInInventory ?? 0} />
        <TopDCom name="Assets sold" count={assetStats?.assetsSold ?? 0} />
        <TopDCom name="Assets amount sold" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountSold || 0)}`} />
        <TopDCom name="Assets amount paid" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountPaid || 0)}`} />
        <TopDCom name="Assets amount outstanding" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountOutstanding || 0)}`} />
      </div>

      <div className="mt-16">
        <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4">
          <Checkbox />
          <p className="text-text flex-1 text-center whitespace-normal break-words">User</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Asset name</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Item amount</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Tenure</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Installment</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Action</p>
        </div>

        {loading ? (
          <div>
            {[...Array(rowsPerPage)].map((_, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-b border-gray-200 p-4 gap-2"
              >
                <div className="w-6 md:w-[5%]">
                  <Skeleton width={24} height={24} />
                </div>
                {[...Array(6)].map((__, skeletonIndex) => (
                  <div key={skeletonIndex} className="flex-1 text-left md:text-center">
                    <Skeleton width="70%" height={20} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : visibleAssets.length === 0 ? (
          <div className="text-center p-4 text-gray-500">
            No repayment records found.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {visibleAssets.map((row) => (
                <div key={row.requestId} className="border rounded-lg p-4 mb-3 bg-white shadow-sm">
                  <p className="font-semibold capitalize">{getFullName(row)}</p>
                  <p className="text-sm text-gray-500 break-words">{row.email}</p>
                  <p><b>Asset name:</b> {row.assetName || 'N/A'}</p>
                  <p><b>Date applied:</b> {formatDate(row.createdAt)}</p>
                  <p><b>Item amount:</b> {formatCurrency(row.totalAmountPayable)}</p>
                  <p><b>Tenure:</b> {row.tenureMonth || 0} Months</p>
                  <p><b>Installment:</b> {row.paidInstallmentCount || 0} of {row.installmentCount || row.tenureMonth || 0}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRequestId(row.requestId);
                      setSelectedAsset(row);
                    }}
                    className="mt-3 w-full rounded-lg bg-[#F8F3FF] px-4 py-3 font-bold text-primary-color"
                  >
                    Review
                  </button>
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              {visibleAssets.map((row) => (
                <div
                  key={row.requestId}
                  className="flex flex-row justify-between items-center bg-white border-b border-gray-200 p-4"
                >
                  <Checkbox />
                  <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    <p className="font-medium capitalize">{getFullName(row)}</p>
                    <p className="text-sm text-gray-500 break-words">{row.email}</p>
                  </div>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{row.assetName || 'N/A'}</p>
                  <p className="flex-1 text-center text-primary-color break-words whitespace-normal min-w-0 px-1">
                    {formatCurrency(row.totalAmountPayable)}
                  </p>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{row.tenureMonth || 0} Months</p>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    {row.paidInstallmentCount || 0} of {row.installmentCount || row.tenureMonth || 0}
                  </p>
                  <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRequestId(row.requestId);
                        setSelectedAsset(row);
                      }}
                      className="rounded-lg bg-[#F8F3FF] px-5 py-3 font-bold text-primary-color"
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-8 mb-10">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg border ${
              currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Previous
          </button>
          <p className="text-text text-sm sm:text-base">
            Page {currentPage + 1} of {totalPages}
          </p>
          <button
            onClick={handleNextPage}
            disabled={!pagination?.hasNext}
            className={`px-4 py-2 rounded-lg border ${
              !pagination?.hasNext
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
